import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { EntityKind, receipts, VendorSummary } from "../api/client";
import MoneyTotals from "../components/MoneyTotals";

type SortKey = "vendor_name" | "receipt_count" | "total_spent_cents";
type SortDir = "asc" | "desc";

/** Configuration for one entity index — Vendors (money out) or Clients (money
 *  in). Both render the same table off the same aggregate endpoint, differing
 *  only in the `kind` filter, wording, and the detail route they link into. */
export interface EntitiesPageProps {
  kind: EntityKind;
  title: string;
  subtitle: string;
  /** Label for the filter input and the "no matches" copy noun. */
  entityLabel: string;
  /** Header for the money column ("Total spent" vs "Total received"). */
  amountLabel: string;
  /** Detail route base, e.g. "/app/vendors" or "/app/clients". */
  basePath: string;
}

function compare(a: VendorSummary, b: VendorSummary, key: SortKey, dir: SortDir): number {
  const av = a[key] ?? (typeof b[key] === "number" ? -Infinity : "");
  const bv = b[key] ?? (typeof a[key] === "number" ? -Infinity : "");
  let n: number;
  if (typeof av === "number" && typeof bv === "number") n = av - bv;
  else n = String(av).localeCompare(String(bv), undefined, { sensitivity: "base" });
  return dir === "asc" ? n : -n;
}

export default function EntitiesPage({
  kind,
  title,
  subtitle,
  entityLabel,
  amountLabel,
  basePath,
}: EntitiesPageProps) {
  const navigate = useNavigate();
  const [items, setItems] = useState<VendorSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState("");
  const [sortKey, setSortKey] = useState<SortKey>("receipt_count");
  const [sortDir, setSortDir] = useState<SortDir>("desc");

  useEffect(() => {
    setLoading(true);
    receipts
      .listVendors(kind)
      .then(setItems)
      .catch((e) => setError(e instanceof Error ? e.message : "Could not load"))
      .finally(() => setLoading(false));
  }, [kind]);

  const sorted = useMemo(() => {
    const needle = filter.trim().toLowerCase();
    const base = needle
      ? items.filter((v) => v.vendor_name.toLowerCase().includes(needle))
      : items;
    return [...base].sort((a, b) => compare(a, b, sortKey, sortDir));
  }, [items, filter, sortKey, sortDir]);

  function toggleSort(next: SortKey) {
    if (sortKey === next) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(next);
      // Numeric columns default to descending (biggest first); text asc.
      setSortDir(next === "vendor_name" ? "asc" : "desc");
    }
  }

  function sortArrow(col: SortKey): string {
    if (sortKey !== col) return "";
    return sortDir === "asc" ? " ▲" : " ▼";
  }

  // Route by vendor_name — the exact field this list groups by, so the
  // detail view shows the same receipts (and count) as the clicked row.
  function routeParam(v: VendorSummary): string {
    return `name:${encodeURIComponent(v.vendor_name)}`;
  }

  return (
    <div className="page">
      <div className="page-header">
        <h1>{title}</h1>
        <p className="page-sub">{subtitle}</p>
      </div>

      <div className="filter-bar">
        <label style={{ flex: 1 }}>
          {entityLabel}
          <input
            type="search"
            placeholder={`Filter by ${entityLabel.toLowerCase()} name…`}
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
        </label>
      </div>

      {error && <div className="page-error">{error}</div>}

      {loading ? (
        <div className="page-empty">Loading…</div>
      ) : sorted.length === 0 ? (
        <div className="page-empty">
          <p>No {entityLabel.toLowerCase()}s match these filters.</p>
        </div>
      ) : (
        <table className="data-table">
          <thead>
            <tr>
              <th
                onClick={() => toggleSort("vendor_name")}
                style={{ cursor: "pointer", userSelect: "none" }}
              >
                {entityLabel}{sortArrow("vendor_name")}
              </th>
              <th
                onClick={() => toggleSort("receipt_count")}
                style={{ cursor: "pointer", userSelect: "none", textAlign: "right" }}
              >
                Receipts{sortArrow("receipt_count")}
              </th>
              <th
                onClick={() => toggleSort("total_spent_cents")}
                style={{ cursor: "pointer", userSelect: "none", textAlign: "right" }}
              >
                {amountLabel}{sortArrow("total_spent_cents")}
              </th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((v) => (
              <tr
                key={v.vendor_name}
                className="clickable"
                onClick={() => navigate(`${basePath}/${routeParam(v)}`)}
              >
                <td>{v.vendor_name}</td>
                <td style={{ textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                  {v.receipt_count}
                </td>
                <td style={{ textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                  <MoneyTotals totals={v.totals} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
